import { Octokit } from "@octokit/rest";
import { env, isGithubSyncEnabled } from "@/env";

/**
 * Phase 6 — GitHub Contents API adapter (LEGACY blob backend).
 *
 * Superseded in production by the BFF / Azure backend (`src/lib/docs-api.ts`,
 * selected in `src/lib/workshop-blob-sync.ts`); retained as a dormant,
 * PAT-driven fallback for the dev/POC path. The AssetLocker (Deliverables) and
 * Captures (PNG snapshots, markdown chat exports, file uploads) stores
 * otherwise live in `idb-keyval` (`src/lib/blob-store.ts` + per-feature meta
 * lists) — local-only: clearing the browser wipes a workshop's data, and a
 * second facilitator can't pick up where the first left off.
 *
 * When this adapter is the active backend, stores keep IDB as the local cache
 * and GitHub becomes the source of truth. The rationale + path scheme +
 * alternatives considered live in `docs/adr/0010-github-persistence.md`.
 *
 * Path scheme:
 *   `workshops/<workshopId>/uploads/<filename>`
 *
 * One repo for the whole programme. Workshop scoping is a directory
 * convention, not a repo boundary, so we don't pay the per-workshop repo
 * creation tax and discovery stays a single `git/trees` walk away.
 *
 * Auth strategy:
 *   - Dev / POC: `VITE_GITHUB_TOKEN` is a PAT with `repo` scope. Plaintext
 *     in env, baked into the bundle — every facilitator needs their own.
 *   - Production: a GitHub App via a BFF (Phase 8). Deferred.
 *
 * This file deliberately constructs the Octokit client lazily — env
 * validation happens at the call site, not at module import time, so
 * `getOctokit()` can throw a useful `GithubStorageError` instead of
 * crashing the bundle when the PAT is missing.
 *
 * The follow-up agents wire this into:
 *   - `src/stores/useAssetLocker.ts` — Deliverables store migration
 *   - `src/stores/useCaptures.ts`    — Captures store migration
 *   - UI indicators for sync state (status pill in the workshop header)
 */

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

export interface GithubAsset {
  /** Path inside the repo, e.g. `"workshops/WS4/uploads/abc-123.png"`. */
  path: string;
  /** SHA from GitHub — required for updates / deletes. */
  sha: string;
  /** Decoded byte size. */
  size: number;
  /** Last commit's ISO timestamp (best-effort — falls back to "now"). */
  updatedAt: string;
}

/**
 * Sane error type so consumers can branch on `status` (HTTP) and `code`
 * (semantic) without sniffing message strings. Mirrors the shape Octokit
 * surfaces in `RequestError`, plus our own `code` field for sync-disabled
 * / config-missing cases that never hit the wire.
 */
export class GithubStorageError extends Error {
  public readonly status: number;
  public readonly code: GithubStorageErrorCode;

  constructor(
    message: string,
    code: GithubStorageErrorCode,
    status = 0,
    cause?: unknown,
  ) {
    super(message);
    this.name = "GithubStorageError";
    this.code = code;
    this.status = status;
    if (cause !== undefined) {
      // `Error.cause` is standard since ES2022 — TS lib targets ESNext.
      (this as { cause?: unknown }).cause = cause;
    }
  }
}

export type GithubStorageErrorCode =
  | "sync_disabled"
  | "not_found"
  | "conflict"
  | "rate_limited"
  | "unauthorized"
  | "network"
  | "unknown";

// ─────────────────────────────────────────────────────────────────────────
// Lazy singleton — `getOctokit()`
// ─────────────────────────────────────────────────────────────────────────

let octokitSingleton: Octokit | null = null;

/**
 * Coerce `VITE_GITHUB_HOST` into a base URL Octokit accepts.
 * Accepts:
 *   - undefined / empty → returns `undefined` (Octokit defaults to
 *     `https://api.github.com`).
 *   - `github.dxc.com/api/v3` → `https://github.dxc.com/api/v3`
 *   - `https://github.dxc.com/api/v3` → unchanged.
 *   - `http://...` → unchanged (we trust the operator on insecure dev).
 *   - `/github` (root-relative) → resolved against `window.location.origin`
 *     so the request stays same-origin and hits Vite's dev proxy (which
 *     forwards to the real GHE server-side, dodging GHE's missing CORS).
 *     Production deploys must own this redirect via a real BFF.
 */
function normaliseHost(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) {
    if (typeof window === "undefined") return undefined;
    return `${window.location.origin}${trimmed}`;
  }
  return `https://${trimmed}`;
}

/**
 * Build (and cache) the Octokit client. Throws `GithubStorageError` with
 * `code: "sync_disabled"` when env vars aren't set — `isGithubSyncEnabled()`
 * is the cheap pre-check consumers should call first.
 *
 * Lazy by design: validating env at import time would crash the bundle on
 * dev machines that legitimately have no PAT yet (Phase 0-5 demo cadence).
 */
export function getOctokit(): Octokit {
  if (octokitSingleton) return octokitSingleton;

  if (!isGithubSyncEnabled()) {
    throw new GithubStorageError(
      "GitHub sync is disabled — set VITE_GITHUB_TOKEN, VITE_GITHUB_OWNER, and VITE_GITHUB_REPO.",
      "sync_disabled",
    );
  }

  octokitSingleton = new Octokit({
    auth: env.VITE_GITHUB_TOKEN,
    // Optional self-hosted GHES override. Default is api.github.com.
    // Normalise: callers may set the host as `github.dxc.com/api/v3` (no
    // scheme) or `https://github.dxc.com/api/v3` (full URL). Octokit's
    // fetch path treats anything without a scheme as a relative path
    // against the current origin → 404s on the dev server. Force a
    // scheme.
    baseUrl: normaliseHost(env.VITE_GITHUB_HOST),
    userAgent: "xaes-mission-control",
  });

  return octokitSingleton;
}

/** Test-only escape hatch. Resets the cached client. */
export function _resetOctokitForTests(): void {
  octokitSingleton = null;
}

// Re-export so consumers only need one import.
export { isGithubSyncEnabled };

// ─────────────────────────────────────────────────────────────────────────
// Repo coordinates helper
// ─────────────────────────────────────────────────────────────────────────

interface RepoCoords {
  owner: string;
  repo: string;
  branch: string;
}

function repoCoords(): RepoCoords {
  // `isGithubSyncEnabled()` guarantees owner + repo. Branch always has a
  // default. The non-null assertions are safe under that pre-check; we
  // throw a clear error otherwise so a misconfigured call doesn't silently
  // hit `api.github.com/repos/undefined/undefined`.
  const owner = env.VITE_GITHUB_OWNER;
  const repo = env.VITE_GITHUB_REPO;
  const branch = env.VITE_GITHUB_BRANCH;
  if (!owner || !repo) {
    throw new GithubStorageError(
      "GitHub owner / repo not set.",
      "sync_disabled",
    );
  }
  return { owner, repo, branch };
}

/** Build the canonical path for a workshop upload. Centralised so the ADR's
 *  path-scheme decision is captured in code. */
export function workshopUploadPath(
  workshopId: string,
  filename: string,
): string {
  // Strip any leading slashes the caller may have included — GitHub paths
  // are repo-rooted and never want a `/` prefix.
  const safeFile = filename.replace(/^\/+/, "");
  return `workshops/${workshopId}/uploads/${safeFile}`;
}

/**
 * Sibling of {@link workshopUploadPath} for the Captures store. Captures
 * (HAH/STR snapshot PNGs, XAES Analyst chat exports) live under a parallel
 * `captures/` subdirectory so a future audit / share flow can address the
 * two surfaces independently and the GitHub repo tree stays visually
 * legible. See `docs/adr/0010-github-persistence.md`.
 */
export function workshopCapturePath(
  workshopId: string,
  filename: string,
): string {
  const safeFile = filename.replace(/^\/+/, "");
  return `workshops/${workshopId}/captures/${safeFile}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Base64 helpers
// ─────────────────────────────────────────────────────────────────────────

/**
 * `btoa` only accepts a "binary string" (each char code 0–255). For
 * arbitrary bytes we chunk through `String.fromCharCode` to stay inside
 * the call-stack-safe argument count (~0xFFFF on most engines, but we
 * pick 0x8000 for headroom).
 */
function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let bin = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    bin += String.fromCharCode(...slice);
  }
  // `btoa` lives on `globalThis` in browsers + jsdom + node 16+.
  return globalThis.btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  // GitHub returns the base64 payload with newlines every 60 chars; `atob`
  // tolerates them but strip just to be safe.
  const clean = b64.replace(/\s+/g, "");
  const bin = globalThis.atob(clean);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  return bytesToBase64(new Uint8Array(buf));
}

// ─────────────────────────────────────────────────────────────────────────
// Retry helper — exponential backoff for 502 / 503 / 429
// ─────────────────────────────────────────────────────────────────────────

const RETRY_STATUS = new Set([429, 502, 503, 504]);
const RETRY_MAX = 3;
const RETRY_START_MS = 500;
const RETRY_CAP_MS = 4_000;

interface OctokitResponseLike {
  status: number;
  // Octokit's ResponseHeaders allows numeric values (e.g. x-ratelimit-*), so
  // include `number` — a narrower `string | undefined` makes the real Octokit
  // response fail the `T extends OctokitResponseLike` constraint, which in turn
  // degrades `res.data` to `unknown` at every call site.
  headers?: Record<string, string | number | undefined>;
  data?: unknown;
}

interface OctokitRequestErrorLike extends Error {
  status?: number;
  response?: OctokitResponseLike;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Wrap a single Octokit call in retry + rate-limit logging. The
 * `x-ratelimit-remaining` header is informational — we warn when below
 * 100 so an attentive operator can pause the workshop, but we never
 * preemptively bail.
 */
async function withRetry<T extends OctokitResponseLike>(
  op: () => Promise<T>,
  label: string,
): Promise<T> {
  let attempt = 0;
  let backoff = RETRY_START_MS;
  // Track the last error so we can surface it after exhausting retries.
  let lastErr: unknown;

  while (attempt <= RETRY_MAX) {
    try {
      const res = await op();
      const remaining = Number(res.headers?.["x-ratelimit-remaining"] ?? NaN);
      if (Number.isFinite(remaining) && remaining < 100) {
        console.warn(
          `[github-storage] ${label}: x-ratelimit-remaining=${remaining} (< 100)`,
        );
      }
      return res;
    } catch (err) {
      lastErr = err;
      const status = (err as OctokitRequestErrorLike).status ?? 0;
      if (!RETRY_STATUS.has(status) || attempt === RETRY_MAX) {
        throw err;
      }
      console.warn(
        `[github-storage] ${label}: status=${status}, retrying in ${backoff}ms (attempt ${attempt + 1}/${RETRY_MAX})`,
      );
      await sleep(backoff);
      backoff = Math.min(backoff * 2, RETRY_CAP_MS);
      attempt += 1;
    }
  }
  // Unreachable — the loop either returns or throws — but TS can't see it.
  throw lastErr;
}

/** Translate an Octokit error into our `GithubStorageError` taxonomy. */
function wrapError(err: unknown, label: string): GithubStorageError {
  if (err instanceof GithubStorageError) return err;
  const e = err as OctokitRequestErrorLike;
  const status = e?.status ?? 0;
  let code: GithubStorageErrorCode;
  switch (status) {
    case 401:
    case 403:
      code = "unauthorized";
      break;
    case 404:
      code = "not_found";
      break;
    case 409:
    case 422:
      code = "conflict";
      break;
    case 429:
      code = "rate_limited";
      break;
    case 0:
      code = "network";
      break;
    default:
      code = "unknown";
  }
  const msg = e?.message ?? "GitHub request failed";
  return new GithubStorageError(`${label}: ${msg}`, code, status, err);
}

// ─────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────

/**
 * Upload a blob to GitHub. Creates a new file or updates an existing one
 * when `existingSha` is supplied (the Contents API uses the SHA as an
 * optimistic-concurrency token — pass the value you got back from a
 * previous `uploadBlob` / `listAssets` call).
 *
 * On a `409` / `422` (SHA mismatch — someone else updated the same path)
 * the caller should re-fetch the asset, merge, and retry. The error code
 * `"conflict"` signals exactly that.
 */
export async function uploadBlob(
  workshopId: string,
  filename: string,
  blob: Blob,
  existingSha?: string,
): Promise<GithubAsset> {
  return uploadToPath(
    workshopId,
    filename,
    workshopUploadPath(workshopId, filename),
    blob,
    existingSha,
    "uploadBlob",
  );
}

/**
 * Sibling of {@link uploadBlob} for the Captures store. Writes the blob
 * to `workshops/<workshopId>/captures/<filename>` (see
 * {@link workshopCapturePath}). Same SHA-based optimistic-concurrency
 * contract as {@link uploadBlob}.
 */
export async function uploadCapture(
  workshopId: string,
  filename: string,
  blob: Blob,
  existingSha?: string,
): Promise<GithubAsset> {
  return uploadToPath(
    workshopId,
    filename,
    workshopCapturePath(workshopId, filename),
    blob,
    existingSha,
    "uploadCapture",
  );
}

/**
 * Shared write path for {@link uploadBlob} and {@link uploadCapture}.
 * Keeps the Octokit call + commit-message scaffolding in one spot so
 * the uploads/captures namespace split doesn't drift over time.
 */
async function uploadToPath(
  workshopId: string,
  filename: string,
  path: string,
  blob: Blob,
  existingSha: string | undefined,
  label: string,
): Promise<GithubAsset> {
  const octokit = getOctokit();
  const { owner, repo, branch } = repoCoords();
  const content = await blobToBase64(blob);

  try {
    const res = await withRetry(
      () =>
        octokit.rest.repos.createOrUpdateFileContents({
          owner,
          repo,
          path,
          branch,
          message: existingSha
            ? `chore(workshops/${workshopId}): update ${filename}`
            : `chore(workshops/${workshopId}): add ${filename}`,
          content,
          sha: existingSha,
        }),
      label,
    );

    const data = res.data;
    const sha = data.content?.sha ?? "";
    const size = data.content?.size ?? blob.size;
    const updatedAt =
      data.commit?.committer?.date ?? new Date().toISOString();

    if (!sha) {
      throw new GithubStorageError(
        `${label}: GitHub returned no SHA`,
        "unknown",
        res.status,
      );
    }

    return { path, sha, size, updatedAt };
  } catch (err) {
    throw wrapError(err, label);
  }
}

/**
 * Fetch a blob by repo-rooted path. Returns `null` on 404 so the offline
 * cache layer can treat "the asset isn't on GitHub yet" as a soft miss
 * rather than a hard error.
 */
export async function downloadBlob(path: string): Promise<Blob | null> {
  const octokit = getOctokit();
  const { owner, repo, branch } = repoCoords();

  try {
    const res = await withRetry(
      () =>
        octokit.rest.repos.getContent({
          owner,
          repo,
          path,
          ref: branch,
        }),
      "downloadBlob",
    );

    // `getContent` returns either a file object or a directory array. We
    // only handle the file case here — callers shouldn't be passing
    // directory paths to `downloadBlob`.
    const data = res.data as
      | { type: string; content?: string; encoding?: string }
      | unknown;
    if (
      typeof data !== "object" ||
      data === null ||
      Array.isArray(data) ||
      (data as { type?: string }).type !== "file"
    ) {
      throw new GithubStorageError(
        `downloadBlob: expected a file at ${path}`,
        "not_found",
        res.status,
      );
    }
    const file = data as { content?: string; encoding?: string };
    if (file.encoding !== "base64" || !file.content) {
      throw new GithubStorageError(
        `downloadBlob: unsupported encoding ${file.encoding ?? "<missing>"} at ${path}`,
        "unknown",
        res.status,
      );
    }
    const bytes = base64ToBytes(file.content);
    // We don't carry the MIME type back from GitHub — the caller owns
    // that. Default to `application/octet-stream`; consumers that know
    // the type (`image/png`, `text/markdown`) can wrap the bytes into
    // a fresh `Blob([bytes], { type })`.
    // Cast through unknown because TS DOM types treat the Uint8Array
    // generic parameter as invariant; the runtime contract is fine.
    return new Blob([bytes as unknown as BlobPart], {
      type: "application/octet-stream",
    });
  } catch (err) {
    const status = (err as OctokitRequestErrorLike).status ?? 0;
    if (status === 404) return null;
    throw wrapError(err, "downloadBlob");
  }
}

/**
 * Delete a blob. The SHA is mandatory — Contents API requires it for
 * the same optimistic-concurrency reason as `uploadBlob`. Caller is
 * expected to have a SHA from a recent `listAssets` / `uploadBlob`.
 */
export async function deleteBlob(path: string, sha: string): Promise<void> {
  const octokit = getOctokit();
  const { owner, repo, branch } = repoCoords();

  try {
    await withRetry(
      () =>
        octokit.rest.repos.deleteFile({
          owner,
          repo,
          path,
          branch,
          message: `chore: delete ${path}`,
          sha,
        }),
      "deleteBlob",
    );
  } catch (err) {
    throw wrapError(err, "deleteBlob");
  }
}

/**
 * List every asset under `workshops/<workshopId>/uploads/`. Returns an
 * empty array when the directory doesn't exist yet (i.e. before the
 * first upload for that workshop). Non-file entries (subdirectories,
 * symlinks) are filtered out.
 *
 * We deliberately do _not_ resolve the "last commit timestamp" per file
 * here — that would cost one extra request per asset on the hot path.
 * `updatedAt` falls back to `now()`, with the contract documented on
 * `GithubAsset`. Consumers that need the real value can look it up on
 * demand.
 */
export async function listAssets(workshopId: string): Promise<GithubAsset[]> {
  return listDir(`workshops/${workshopId}/uploads`, "listAssets");
}

/**
 * Sibling of {@link listAssets} for the Captures store. Walks the
 * `workshops/<workshopId>/captures/` subdirectory; same soft-404
 * semantics ("no captures yet" → empty array, not an error).
 */
export async function listCaptures(
  workshopId: string,
): Promise<GithubAsset[]> {
  return listDir(`workshops/${workshopId}/captures`, "listCaptures");
}

/**
 * Shared directory walker used by both {@link listAssets} and
 * {@link listCaptures}. Factored out so the uploads/captures split
 * doesn't duplicate the Octokit + error-mapping plumbing.
 */
async function listDir(
  dir: string,
  label: string,
): Promise<GithubAsset[]> {
  const octokit = getOctokit();
  const { owner, repo, branch } = repoCoords();

  try {
    const res = await withRetry(
      () =>
        octokit.rest.repos.getContent({
          owner,
          repo,
          path: dir,
          ref: branch,
        }),
      label,
    );

    const data = res.data;
    if (!Array.isArray(data)) {
      // GitHub returns a single file object when `path` happens to point
      // at a file, not a directory. For our path scheme that shouldn't
      // happen, but treat it as "no listable assets" defensively.
      return [];
    }

    const now = new Date().toISOString();
    return data
      .filter((entry) => entry.type === "file")
      .map((entry) => ({
        path: entry.path,
        sha: entry.sha,
        size: entry.size ?? 0,
        updatedAt: now,
      }));
  } catch (err) {
    const status = (err as OctokitRequestErrorLike).status ?? 0;
    // Empty workshop — no uploads yet — surfaces as a 404. That's a
    // soft case, not an error.
    if (status === 404) return [];
    throw wrapError(err, label);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Commit history (RT-VERSIONING) — bodies for the P0.8 signature stubs
// ─────────────────────────────────────────────────────────────────────────

/** One entry in a path's commit history (newest first). */
export interface GithubCommit {
  /** Commit SHA — the ref to fetch the file's blob as-of this point. */
  sha: string;
  /** First line of the commit message. */
  message: string;
  /** Author display name (best-effort: commit author → login → "unknown"). */
  author: string;
  /** ISO timestamp of the commit; "" when GitHub omitted it. */
  date: string;
}

/**
 * List the commits that touched `path`, newest first. Returns `[]` when
 * GitHub sync is disabled — the local-first / mock posture, where the
 * VersionHistoryModal renders an empty timeline. The populated timeline is
 * a deploy-gated verification (DEP-VERSIONING); a real dev PAT exercises it
 * locally with no Azure. Signature frozen in P0.8; body filled here.
 */
export async function listCommitsForPath(path: string): Promise<GithubCommit[]> {
  if (!isGithubSyncEnabled()) return [];
  const octokit = getOctokit();
  const { owner, repo, branch } = repoCoords();
  try {
    const res = await withRetry(
      () =>
        octokit.rest.repos.listCommits({
          owner,
          repo,
          sha: branch,
          path,
          per_page: 50,
        }),
      "listCommitsForPath",
    );
    const commits = res.data as Array<{
      sha: string;
      commit: {
        message?: string;
        author?: { name?: string; date?: string } | null;
      };
      author?: { login?: string } | null;
    }>;
    return commits.map((c) => ({
      sha: c.sha,
      message: (c.commit.message ?? "").split("\n")[0] ?? "",
      author: c.commit.author?.name ?? c.author?.login ?? "unknown",
      date: c.commit.author?.date ?? "",
    }));
  } catch (err) {
    throw wrapError(err, "listCommitsForPath");
  }
}

/**
 * Fetch a file's blob as it existed at commit `sha`. Returns `null` when
 * sync is disabled, or the path didn't exist at that commit (soft miss).
 * Mirrors {@link downloadBlob} but pins the ref to a commit rather than
 * the branch head — the read half of the version-history view/restore.
 * Signature frozen in P0.8; body filled here.
 */
export async function getBlobAtCommit(
  path: string,
  sha: string,
): Promise<Blob | null> {
  if (!isGithubSyncEnabled()) return null;
  const octokit = getOctokit();
  const { owner, repo } = repoCoords();
  try {
    const res = await withRetry(
      () => octokit.rest.repos.getContent({ owner, repo, path, ref: sha }),
      "getBlobAtCommit",
    );
    const data = res.data as
      | { type?: string; content?: string; encoding?: string }
      | unknown;
    if (
      typeof data !== "object" ||
      data === null ||
      Array.isArray(data) ||
      (data as { type?: string }).type !== "file"
    ) {
      return null;
    }
    const file = data as { content?: string; encoding?: string };
    if (file.encoding !== "base64" || !file.content) return null;
    const bytes = base64ToBytes(file.content);
    return new Blob([bytes as unknown as BlobPart], {
      type: "application/octet-stream",
    });
  } catch (err) {
    const status = (err as OctokitRequestErrorLike).status ?? 0;
    if (status === 404) return null;
    throw wrapError(err, "getBlobAtCommit");
  }
}
